export function formatBytes(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

export function formatTime(ms?: number): string {
  if (ms === undefined || ms === null) return '-';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

export function formatDate(date: Date): string {
  return date.toLocaleString('cs-CZ', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function formatPathLabel(path: string): string {
  const parts = path.split('.');
  if (parts.length <= 2) return path;
  return parts.slice(-2).join('.');
}

export function downloadJSON(data: any, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function base64ToBlobUrl(base64: string, mimeType: string): string {
  const byteChars = atob(base64);
  const byteArr = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
  const blob = new Blob([byteArr], { type: mimeType });
  return URL.createObjectURL(blob);
}

export function downloadBase64(base64: string, filename: string, mimeType: string) {
  const url = base64ToBlobUrl(base64, mimeType);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/* ───── IndexedDB persistence ───── */
const DB_NAME = 'ocr_comparator';
const STORE_NAME = 'pinned_results';
const DB_VERSION = 1;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Save pinned results to IndexedDB. Strips GCP raw response to save space. */
export async function savePinnedResults(results: any[], pinnedIds: Set<string>): Promise<boolean> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.clear();
    const pinned = results
      .filter(r => pinnedIds.has(r.fileGroupId))
      .map(r => ({
        ...r,
        documentAi: { ...r.documentAi, raw: undefined },
      }));
    for (const r of pinned) {
      store.put(r);
    }
    return new Promise((resolve) => {
      tx.oncomplete = () => { db.close(); resolve(true); };
      tx.onerror = () => { db.close(); resolve(false); };
    });
  } catch {
    return false;
  }
}

/** Load pinned results from IndexedDB. Converts timestamp strings back to Date. */
export async function loadPinnedResults(): Promise<{ results: any[], pinnedIds: Set<string> }> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();
    return new Promise((resolve) => {
      req.onsuccess = () => {
        db.close();
        const parsed = req.result || [];
        const results = parsed.map((r: any) => ({ ...r, timestamp: new Date(r.timestamp) }));
        const pinnedIds = new Set<string>(results.map((r: any) => r.fileGroupId));
        resolve({ results, pinnedIds });
      };
      req.onerror = () => {
        db.close();
        resolve({ results: [], pinnedIds: new Set() });
      };
    });
  } catch {
    return { results: [], pinnedIds: new Set() };
  }
}

/** Clear all pinned data from IndexedDB. */
export async function clearPinnedStorage(): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
    await new Promise<void>((resolve) => {
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); resolve(); };
    });
  } catch {}
}

/** Migrate old localStorage data to IndexedDB (one-time) */
export function migrateFromLocalStorage(): any[] | null {
  try {
    const raw = localStorage.getItem('ocr_pinned_results');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    localStorage.removeItem('ocr_pinned_results');
    return parsed;
  } catch {
    return null;
  }
}
